<template>
  <div class="min-h-full subiekt-panel">
    <button @click="$router.push('/dashboard/subiekt/dokumenty')" type="button"
      class="inline-flex items-center gap-x-1.5 text-sm text-gray-500 transition duration-200 hover:text-gray-700 mb-4">
      <ArrowNarrowLeftIcon class="h-4 w-4" aria-hidden="true" />
      Wróć do dokumentów
    </button>

    <div class="mb-6">
      <h1 class="text-lg font-medium text-gray-900">Akordówka</h1>
      <p class="mt-1 max-w-2xl text-sm text-gray-500">
        Jedna akordówka tworzy komplet powiązanych dokumentów. Wysyła się je po kolei —
        następny odblokowuje się dopiero po wysłaniu poprzedniego.
      </p>
    </div>

    <section class="rounded-xl bg-white ring-1 ring-gray-900 ring-opacity-5 shadow-sm px-5 py-4 mb-4">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label for="tryb" class="block text-xs font-medium text-gray-600">Rodzaj</label>
          <select id="tryb" v-model="form.tryb"
            class="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500">
            <option value="produkcja">Produkcja własna</option>
            <option value="naprawa">Naprawa tapicerowana</option>
          </select>
        </div>
        <div>
          <label for="data" class="block text-xs font-medium text-gray-600">Data dokumentów</label>
          <input id="data" v-model="form.date" type="date"
            class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
        </div>
        <div>
          <label for="numer" class="block text-xs font-medium text-gray-600">Numer akordówki</label>
          <input id="numer" v-model="form.numerAkordowki" type="text" placeholder="AK/2026/07/14"
            class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
        </div>
      </div>

      <p class="mt-3 text-sm text-gray-500">
        Powstanie: <span class="font-medium text-gray-700">{{ chainLabel }}</span>
      </p>
    </section>

    <section class="rounded-xl bg-white ring-1 ring-gray-900 ring-opacity-5 shadow-sm px-5 py-4 mb-4">
      <h2 class="text-xs font-medium uppercase tracking-wide text-gray-500">Pozycje</h2>

      <!-- Wyszukiwanie w locie. Combobox z HeadlessUI, bo reszta systemu już go używa
           i zachowanie klawiatury (strzałki, Enter, Esc) dostajemy gotowe. -->
      <Combobox as="div" nullable :model-value="null" @update:modelValue="addVariant" class="mt-3">
        <div class="relative">
          <ComboboxInput
            ref="comboInput"
            @change="onQueryChange($event.target.value)"
            :display-value="() => query"
            placeholder="Zacznij pisać nazwę produktu, symbol albo EAN…"
            class="w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
          <div class="absolute inset-y-0 right-0 flex items-center pr-3">
            <RefreshIcon v-if="searching" class="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />
            <SearchIcon v-else class="h-4 w-4 text-gray-400" aria-hidden="true" />
          </div>

          <!-- Lista musi istnieć od razu po wpisaniu drugiego znaku, nawet zanim wrócą
               wyniki. Gdy renderowała się dopiero z wynikami, HeadlessUI w międzyczasie
               zamykał pustą listę i spóźniona odpowiedź nie miała się gdzie pokazać. -->
          <ComboboxOptions v-if="query.trim().length > 1"
            class="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <li v-if="searching && searchResults.length === 0"
              class="flex items-center gap-2 px-3 py-2 text-sm text-gray-500">
              <RefreshIcon class="h-4 w-4 animate-spin" aria-hidden="true" />
              Szukam…
            </li>
            <li v-else-if="!searching && searchResults.length === 0"
              class="px-3 py-2 text-sm text-gray-500">Nic nie znaleziono dla „{{ query.trim() }}".</li>

            <ComboboxOption v-for="w in searchResults" :key="w.productId + w.variantId" :value="w" as="template"
              v-slot="{ active }">
              <li :class="[active ? 'bg-indigo-50' : '', 'flex cursor-pointer items-center gap-3 px-3 py-2']">
                <img :src="w.image" :alt="w.nazwaProduktu"
                  class="h-10 w-10 flex-shrink-0 rounded-md object-contain subiekt-thumb" />
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm text-gray-900">
                    {{ w.nazwaProduktu }}
                    <span v-if="w.nazwaWariantu"
                      class="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">{{ w.nazwaWariantu }}</span>
                  </p>
                  <p class="mt-0.5 truncate text-xs">
                    <span v-if="w.symbolTowaru" class="font-mono text-gray-500">{{ w.symbolTowaru }}</span>
                    <span v-else class="rounded bg-red-100 px-1.5 py-0.5 font-medium text-red-800">brak symbolu Subiekta</span>
                  </p>
                </div>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </div>
      </Combobox>

      <table class="mt-3 w-full text-sm">
        <thead>
          <tr class="border-b border-gray-200">
            <th class="py-2 pr-3 text-left font-medium text-gray-500">Symbol Subiekta</th>
            <th class="py-2 pr-3 text-left font-medium text-gray-500">Nazwa</th>
            <th class="py-2 pr-3 text-right font-medium text-gray-500">Ilość</th>
            <th class="py-2 pr-3 text-right font-medium text-gray-500">Cena netto</th>
            <th class="py-2 text-right font-medium text-gray-500">Wartość</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in form.pozycje" :key="index" class="border-b border-gray-100">
            <td class="py-2 pr-3 align-top whitespace-nowrap">
              <span v-if="row.symbolTowaru" class="font-mono text-xs text-gray-500">{{ row.symbolTowaru }}</span>
              <span v-else class="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">brak symbolu</span>
            </td>
            <td class="py-2 pr-3 align-top text-gray-900">
              {{ row.nazwaProduktu }}
              <span v-if="row.nazwaWariantu"
                class="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">{{ row.nazwaWariantu }}</span>
            </td>
            <td class="py-2 pr-3">
              <input v-model.number="row.iloscJm" type="number" min="1"
                class="w-20 rounded-md border-gray-300 py-1.5 px-2 text-right text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
            </td>
            <td class="py-2 pr-3">
              <input v-model.number="row.cenaNetto" type="number" min="0" step="0.01" placeholder="0,00"
                class="w-28 rounded-md border-gray-300 py-1.5 px-2 text-right text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500" />
            </td>
            <td class="py-2 text-right text-gray-700 doc-tabular whitespace-nowrap">{{ rowValue(row) }}</td>
            <td class="py-2 pl-3 text-right">
              <button @click="form.pozycje.splice(index, 1)" type="button"
                class="text-xs text-red-600 transition duration-200 hover:text-red-500">usuń</button>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4" class="py-2 text-sm font-medium text-gray-900">Razem</td>
            <td class="py-2 text-right text-sm font-medium text-gray-900 doc-tabular whitespace-nowrap">{{ totalValue }}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>

      <p v-if="form.pozycje.length === 0" class="mt-3 text-sm text-gray-500">
        Brak pozycji — wyszukaj produkt powyżej.
      </p>
    </section>

    <!-- Kwoty nie dojdą dziś do Subiekta — most ustawia na pozycji wyłącznie ilość.
         Lepiej powiedzieć to wprost, niż pozwolić komuś założyć, że wycena zadziałała. -->
    <div class="mb-6 flex gap-3 rounded-xl bg-yellow-50 px-4 py-3.5">
      <ExclamationIcon class="h-5 w-5 flex-shrink-0 text-yellow-500 doc-icon-nudge" aria-hidden="true" />
      <div class="text-sm text-yellow-800">
        <p class="font-medium text-yellow-900">Kwoty zapisują się w systemie, ale nie trafią jeszcze do Subiekta</p>
        <p class="mt-0.5">
          Most ustawia na pozycji dokumentu wyłącznie ilość. Do czasu zmiany w moście
          kwotę trzeba nanieść w Subiekcie ręcznie.
        </p>
      </div>
    </div>

    <button @click="create()" :disabled="saving || !canCreate" type="button"
      :class="[(saving || !canCreate) ? 'cursor-not-allowed opacity-50' : 'hover:bg-indigo-500',
               'doc-press inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm']">
      <RefreshIcon v-if="saving" class="h-4 w-4 animate-spin" aria-hidden="true" />
      Utwórz dokumenty
    </button>
  </div>
</template>

<script>
import axios from 'axios';
import { subiektPost } from '@/subiektApi';
import moment from 'moment';
import { RefreshIcon, ArrowNarrowLeftIcon, ExclamationIcon, SearchIcon } from '@heroicons/vue/solid';
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption } from '@headlessui/vue';
import { invalidateDocuments } from '@/subiektDocumentsCache';

export default {
  name: 'SubiektProduction',
  components: { RefreshIcon, ArrowNarrowLeftIcon, ExclamationIcon, SearchIcon,
    Combobox, ComboboxInput, ComboboxOptions, ComboboxOption },
  data() {
    return {
      saving: false,
      form: {
        tryb: 'produkcja',
        date: moment().format('YYYY-MM-DD'),
        numerAkordowki: '',
        pozycje: [],
      },
      query: '',
      searchTimer: null,
      searching: false,
      searchResults: [],
    };
  },
  computed: {
    chainLabel() {
      return this.form.tryb === 'naprawa'
        ? 'PZ (produkcja) → MM (Mianowice)'
        : 'RW (produkcja) → PW (wyroby gotowe) → MM (Mianowice)';
    },
    filledRows() {
      return this.form.pozycje.filter(r => Number(r.iloscJm) > 0);
    },
    canCreate() {
      return this.form.numerAkordowki.trim().length > 0 && this.filledRows.length > 0;
    },
    totalValue() {
      const total = this.filledRows.reduce((s, r) => s + (Number(r.cenaNetto) || 0) * Number(r.iloscJm || 0), 0);
      return this.formatMoney(total);
    },
  },
  methods: {
    formatMoney(value) {
      return `${(Math.round(value * 100) / 100).toFixed(2)} zł`;
    },
    rowValue(row) {
      return this.formatMoney((Number(row.cenaNetto) || 0) * Number(row.iloscJm || 0));
    },
    // Szukamy w trakcie pisania, ale z opóźnieniem — bez tego każda litera
    // to osobne zapytanie do Elasticsearcha.
    onQueryChange(value) {
      this.query = value;
      if(this.searchTimer) clearTimeout(this.searchTimer);
      if(this.query.trim().length < 2) {
        this.searchResults = [];
        return;
      }
      this.searchTimer = setTimeout(() => this.search(), 300);
    },
    async search() {
      const fraza = this.query.trim();
      if(fraza.length < 2) return;
      this.searching = true;
      try {
        const res = await axios.post(`${this.$store.state.apiLink}/search`, {
          index: 'jan-art-products',
          collection: 'Products',
          phrase: fraza,
          searchFields: ['names','models','skus','eans','id','categories','manufacturers','variantsIds','baselinkerIds'],
        });
        if(!res.data.success) throw res.data.log || res.data.error;

        // Odpowiedź mogła się rozminąć z tym, co użytkownik zdążył dopisać.
        if(this.query.trim() !== fraza) return;

        const results = [];
        for(const product of (res.data.found || [])) {
          for(const [variantId, variant] of Object.entries(product?.variants || {})) {
            results.push({
              productId: product.id,
              variantId,
              nazwaProduktu: product.name || '',
              nazwaWariantu: this.stripPrefix(product.name, variant?.name),
              symbolTowaru: (variant?.subiekt || variant?.sku || '').toString().trim() || null,
              image: this.imageFor(product, variant),
            });
          }
        }
        this.searchResults = results.slice(0, 60);
      } catch (error) {
        console.log(error);
      } finally {
        this.searching = false;
      }
    },
    // Zdjęcie wariantu ma pierwszeństwo nad zdjęciem produktu; gdy nie ma żadnego,
    // wchodzi ten sam placeholder, którego używa lista produktów.
    imageFor(product, variant) {
      const wariant = (variant?.image?.url || '').toString();
      if(wariant.length > 0) return wariant;
      const produkt = (product?.image?.url || '').toString();
      if(produkt.length > 0) return produkt;
      return `https://api.dicebear.com/7.x/initials/svg?seed=Product${product?.id || ''}`;
    },
    // Nazwa wariantu w kartotece zwykle powtarza nazwę produktu — bez tego
    // w tabeli byłoby „Sofa Bergen Sofa Bergen - Biała".
    stripPrefix(productLabel, variantLabel) {
      const produkt = (productLabel || '').trim();
      const wariant = (variantLabel || '').trim();
      if(produkt.length === 0 || wariant.length === 0) return wariant;
      if(!wariant.toLowerCase().startsWith(produkt.toLowerCase())) return wariant;
      return wariant.slice(produkt.length).replace(/^[\s\-–—:,]+/, '').trim();
    },
    addVariant(w) {
      if(!w) return;
      this.query = '';
      this.searchResults = [];
      // modelValue zostaje null, więc HeadlessUI nie przerysuje pola samo —
      // czyścimy je wprost i zostawiamy fokus, żeby dało się szukać dalej.
      const input = this.$refs.comboInput?.$el || this.$refs.comboInput;
      if(input) { input.value = ''; input.focus(); }
      const istnieje = this.form.pozycje.find(r => r.productId === w.productId && r.variantId === w.variantId);
      if(istnieje) { istnieje.iloscJm = Number(istnieje.iloscJm || 0) + 1; return; }
      this.form.pozycje.push({ ...w, iloscJm: 1, cenaNetto: null });
    },
    async create() {
      if(this.saving || !this.canCreate) return;
      this.saving = true;
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/produkcja/batch', {
          date: this.form.date,
          tryb: this.form.tryb,
          numerAkordowki: this.form.numerAkordowki.trim(),
          pozycje: this.filledRows,
          user: this.$store.state.userData,
        });
        if(!res.data.success) throw res.data.log || res.data.error;

        this.$store.commit('setNotification', {
          show: true, head: 'Dokumenty utworzone.', subheader: res.data.log, success: true,
        });
        // Panel trzyma dzień w pamięci — bez tego nowe dokumenty by się nie pokazały.
        invalidateDocuments(this.form.date);
        this.$router.push('/dashboard/subiekt/dokumenty');
      } catch (error) {
        console.log(error);
        this.$store.commit('setNotification', {
          show: true, head: 'Nie udało się utworzyć dokumentów.',
          subheader: typeof error === 'string' ? error : 'Coś poszło nie tak.', success: false,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Tailwind 2 bez JIT nie generuje wartości dowolnych. */
.doc-press {
  transition-property: transform, background-color, box-shadow;
  transition-duration: 200ms;
  transition-timing-function: cubic-bezier(0.2, 0, 0, 1);
}
.doc-press:active {
  transform: scale(0.96);
}
.doc-tabular {
  font-variant-numeric: tabular-nums;
}
.doc-icon-nudge {
  margin-top: 1px;
}
.subiekt-panel {
  -webkit-font-smoothing: antialiased;
}
/* Delikatny obrys na zdjęciach — bez niego jasne produkty zlewają się z tłem.
   Czysta czerń o niskiej przezroczystości, nie szarość, żeby nie brudzić krawędzi. */
.subiekt-thumb {
  outline: 1px solid rgba(0, 0, 0, 0.1);
  outline-offset: -1px;
  background-color: #fff;
}
</style>
